// lib/hooks/use-google-staff-signin.js — the Google Workspace SSO dance,
// once, for every staff-facing page that has a sign-in screen.
//
// Extracted verbatim from components/login-page.jsx so Zephyr, the standalone
// front-office site, signs in exactly as the SPA does: same public config, same
// GIS options, same staff-only endpoint, same status wording. Two copies of
// this would drift, and the failure mode is "nobody can sign in on one page".
//
// The GIS client script tag lives in each shell HTML (async defer,
// nonce-stamped by serveWithNonce). We poll for `window.google.accounts.id`
// after mount, fetch /auth/public-config for the client id + workspace domain,
// initialize GIS, and render the official Google button into a div the caller
// owns via `buttonRef`.
//
// On credential we POST to /auth/google/staff. The server validates the id
// token, sets the auth cookie, and returns the user; the caller's
// onAuthenticated(user) then flips its own auth state — no page reload, so an
// in-progress route/hash survives.
//
// Usage:
//   const { buttonRef, status, signing } = useGoogleStaffSignIn({ onAuthenticated });
//   <div ref={buttonRef} /> …and render `status` in an aria-live region.

import { useEffect, useRef, useState } from 'react';

export const PUBLIC_CONFIG_ENDPOINT = '/auth/public-config';
export const STAFF_SIGNIN_ENDPOINT = '/auth/google/staff';

// Every string either screen can show. Kept here so both say the same thing.
export const SIGNIN_MESSAGES = {
  configUnavailable: 'Could not load Google sign-in configuration.',
  notConfigured: 'Google sign-in is not configured for this practice.',
  initFailed: 'Could not initialize Google sign-in.',
  cancelled: 'Google sign-in was cancelled.',
  signingIn: 'Signing in…',
  failed: 'Google sign-in failed.',
  connectionPrefix: 'Connection error: ',
};

// Match the v2 settings — FedCM disabled until verified on prod (lvh.me dev
// hosts trip Chrome's FedCM CORS check; popup with COOP
// same-origin-allow-popups works reliably everywhere).
// `hd` must be undefined rather than '' when no workspace domain is
// configured: GIS reads an empty string as "restrict to the empty domain".
export function gisInitOptions(cfg, callback) {
  return {
    client_id: cfg.googleClientId,
    callback,
    ux_mode: 'popup',
    auto_select: false,
    hd: (cfg && cfg.workspaceDomain) || undefined,
    essential_claims: 'amr',
    use_fedcm_for_prompt: false,
    use_fedcm_for_button: false,
  };
}

export const GIS_BUTTON_OPTIONS = {
  type: 'standard',
  theme: 'outline',
  size: 'large',
  text: 'signin_with',
  shape: 'rectangular',
  logo_alignment: 'center',
  width: 280,
};

export function useGoogleStaffSignIn({ onAuthenticated } = {}) {
  const [status, setStatus] = useState('');
  const [signing, setSigning] = useState(false);
  const buttonRef = useRef(null);
  const initialized = useRef(false);

  useEffect(() => {
    let cancelled = false;
    let pollTimer = null;

    async function init() {
      if (initialized.current) return;
      const gis = typeof window !== 'undefined' ? window.google : null;
      if (!gis || !gis.accounts || !gis.accounts.id) {
        pollTimer = setTimeout(init, 200);
        return;
      }
      try {
        const r = await fetch(PUBLIC_CONFIG_ENDPOINT, { credentials: 'same-origin' });
        if (!r.ok) {
          if (!cancelled) setStatus(SIGNIN_MESSAGES.configUnavailable);
          return;
        }
        const cfg = await r.json();
        if (!cfg.googleClientId) {
          if (!cancelled) setStatus(SIGNIN_MESSAGES.notConfigured);
          return;
        }
        if (cancelled) return;
        gis.accounts.id.initialize(gisInitOptions(cfg, handleCredential));
        if (buttonRef.current) {
          gis.accounts.id.renderButton(buttonRef.current, { ...GIS_BUTTON_OPTIONS });
        }
        initialized.current = true;
      } catch (e) {
        if (!cancelled) setStatus(SIGNIN_MESSAGES.initFailed);
      }
    }

    async function handleCredential(response) {
      if (!response || !response.credential) {
        setStatus(SIGNIN_MESSAGES.cancelled);
        return;
      }
      setSigning(true);
      setStatus(SIGNIN_MESSAGES.signingIn);
      try {
        const r = await fetch(STAFF_SIGNIN_ENDPOINT, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          credentials: 'same-origin',
          body: JSON.stringify({ idToken: response.credential }),
        });
        const data = await r.json().catch(() => ({}));
        if (!r.ok) {
          setStatus(data.error || SIGNIN_MESSAGES.failed);
          setSigning(false);
          return;
        }
        setStatus('');
        // Let the caller flip its auth state so the page renders without a
        // hard reload (which would lose any in-progress route hash).
        onAuthenticated?.(data.user || null);
      } catch (e) {
        setStatus(SIGNIN_MESSAGES.connectionPrefix + (e?.message || 'unknown'));
        setSigning(false);
      }
    }

    init();
    return () => { cancelled = true; if (pollTimer) clearTimeout(pollTimer); };
  }, [onAuthenticated]);

  return { buttonRef, status, signing };
}
